/**
 * dor-bogo.js
 * TypeScript-flavored runtime (JSDoc) for BOGO cart handling.
 *
 * Responsibilities:
 * - Read a global config object `window.DOR_BOGO_CONFIG` (injected from Liquid)
 * - Poll or listen to cart changes and evaluate matches for group1 and group2
 * - Add or remove a gift line (100% discounted) using Shopify AJAX Cart API
 *
 * Notes:
 * - This file is plain JS but uses JSDoc types so it can be type-checked if copied
 *   into a TypeScript project.
 */

(() => {
  /**
   * Utility: safe access to config
   * @returns {any}
   */
  const getConfig = () => window.DOR_BOGO_CONFIG || {};

  /**
   * Resolve metafield value which could be JSON or comma-separated ids
   * @param {any} mf
   * @returns {Array<string>}
   */
  const parseListMetafield = (mf) => {
    if (!mf && mf !== 0) return [];

    // If it's already an array (e.g. injected from Liquid as a JSON array)
    if (Array.isArray(mf)) {
      return mf.map(el => normalizeId((el?.id) ? String(el.id) : String(el))).filter(Boolean);
    }

    // If it's an object that directly contains an id (e.g. variant object)
    if (typeof mf === 'object') {
      try {
        if (mf.jsonValue) return Array.isArray(mf.jsonValue) ? mf.jsonValue.map(String) : [String(mf.jsonValue)];
        if (mf.value) {
          try {
            const parsed = JSON.parse(mf.value);
            if (Array.isArray(parsed)) return parsed.map(String);
            return [String(parsed)];
          } catch {
            return String(mf.value).split(',').map(s => s.trim()).filter(Boolean);
          }
        }
        if (mf.id) return [normalizeId(String(mf.id))];
      } catch {
        return [];
      }
    }

    // If it's a primitive (string/number)
    if (typeof mf === 'string') {
      try {
        const parsed = JSON.parse(mf);
        if (Array.isArray(parsed)) return parsed.map(p => normalizeId(String(p)));
        return [normalizeId(String(parsed))];
      } catch {
        return mf.split(',').map(s => normalizeId(s.trim())).filter(Boolean);
      }
    }

    if (typeof mf === 'number') return [String(mf)];
    return [];
  };

  /**
   * Normalize id-like values: trim, and if it's a Shopify GID extract the numeric id
   * @param {string} v
   */
  const normalizeId = (v) => {
    if (!v && v !== 0) return '';
    const s = String(v).trim();
    // GID form: gid://shopify/ProductVariant/12345
    const m = s.match(/\/(\d+)$/);
    if (m) return m[1];
    // plain digits
    const digits = s.match(/\d+/);
    return digits ? digits[0] : s;
  };

  /**
   * Resolve a single variant id from several possible shapes
   * @param {any} val
   * @returns {string}
   */
  const resolveVariantId = (val) => {
    if (!val && val !== 0) return '';
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (Array.isArray(val) && val.length > 0) {
      const first = val[0];
      return normalizeId((first?.id) ? String(first.id) : String(first));
    }
    if (typeof val === 'object') {
      if (val.id) return normalizeId(String(val.id));
      if (val.jsonValue) return normalizeId(String(val.jsonValue));
      if (val.value) return normalizeId(String(val.value));
    }
    return '';
  };

  /**
   * Detect if a cart item is a BOGO gift line.
   * Priority 1: explicit gift marker property (private) if present.
   * Priority 2: fallback to 100% discount detection.
   * @param {any} item - Cart line item
   * @param {string} giftVariantId - Expected gift variant ID
   * @param {string} giftMarker - Gift marker property key (e.g., _dor_bogo_gift)
   * @returns {boolean}
   */
  const isGiftLine = (item, giftVariantId, giftMarker = '_dor_bogo_gift') => {
    if (!item) return false;

    // Variant must match the configured gift variant
    if (String(item.variant_id) !== String(giftVariantId)) return false;

    // First: honor explicit gift marker if present
    if (giftMarker && item.properties && Object.prototype.hasOwnProperty.call(item.properties, giftMarker)) {
      return Boolean(item.properties[giftMarker]);
    }

    // Fallback: detect by ~100% discount
    const originalPrice = Number(item.original_line_price || item.line_price || 0);
    const finalPrice = Number(item.final_line_price || item.line_price || 0);
    if (originalPrice === 0) return false; // cannot determine
    const discountPercent = ((originalPrice - finalPrice) / originalPrice) * 100;
    return discountPercent >= 99.9;
  };

  /**
   * Parse a numeric config value robustly and return a finite number or default
   * @param {any} v
   * @param {number} def
   */
  const parseNumberConfig = (v, def) => {
    if (v === null || v === undefined) return def;
    let val = v;
    // If object with .value
    if (typeof val === 'object') {
      if (val.value !== undefined) val = val.value;
      else if (val.jsonValue !== undefined) val = val.jsonValue;
    }
    // If array, pick first
    if (Array.isArray(val)) val = val[0];
    // Empty string -> default
    if (typeof val === 'string' && val.trim() === '') return def;
    const n = Number(val);
    if (!isFinite(n)) return def;
    return n;
  };

  /**
   * Map of cart line id to helpful data
   * @typedef {{id:number,variantId:string,productId:string,productType:string,tags:Array<string>,quantity:number,lineIndex:number}} LineInfo
   */

  /**
   * Build a normalized list of items from the cart JSON
   * @param {any} cart
   * @returns {Array<LineInfo>}
   */
  const normalizeCart = (cart) => {
    if (!cart?.items) return [];
    return cart.items.map((item, idx) => ({
      id: item.id,
      variantId: String(item.variant_id || item.variant?.id || ''),
      productId: String(item.product_id || item.product?.id || ''),
      productType: String(item.product_type || item.product?.product_type || ''),
      tags: (item.tags || item.product?.tags || '').split?.(',').map(t => t.trim()).filter(Boolean) || [],
      quantity: Number(item.quantity || 0),
      lineIndex: idx
    }));
  };

  /**
   * Check how many matches for a group exist in cart
   * @param {Array<LineInfo>} lines
   * @param {Object} group
   * @param {Array<string>} group.variants - variant ids (strings)
   * @param {Array<string>} group.list - generic values for tag/type/product id
   * @param {string} group.match - 'variant'|'tag'|'type'|'product'
   * @returns {number} total matched quantity
   */
  const countMatches = (lines, group) => {
    if (!group) return 0;
    let total = 0;
    const { variants = [], list = [], match = '' } = group;
    const matchType = match.toLowerCase();

    lines.forEach(line => {
      let matched = false;
      if (variants.length && variants.includes(line.variantId)) matched = true;
      if (!matched && matchType === 'tag' && list.some(v => line.tags.includes(v))) matched = true;
      if (!matched && matchType === 'type' && list.includes(line.productType)) matched = true;
      if (!matched && matchType === 'product' && list.includes(line.productId)) matched = true;
      if (!matched && matchType === 'variant' && list.includes(line.variantId)) matched = true;
      if (matched) total += line.quantity;
    });
    return total;
  };

  /**
   * Fetch cart JSON via AJAX
   * @returns {Promise<any>}
   */
  const fetchCart = () =>
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());

  /**
   * Simple product cache for product handles
   * @type {Object<string, any>}
   */
  const PRODUCT_CACHE = {};

  /**
   * Fetch product JSON by handle from /products/{handle}.js
   * @param {string} handle
   * @returns {Promise<any>}
   */
  const fetchProductByHandle = async (handle) => {
    if (!handle) return null;
    if (PRODUCT_CACHE[handle]) return PRODUCT_CACHE[handle];

    try {
      const r = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!r.ok) {
        PRODUCT_CACHE[handle] = null;
        return null;
      }
      const prod = await r.json();
      PRODUCT_CACHE[handle] = prod || null;
      return prod;
    } catch {
      PRODUCT_CACHE[handle] = null;
      return null;
    }
  };

  /**
   * Add gift variant as a separate line with quantity q and optional properties.
   * Dispatches events to notify theme components and reloads page if on cart page.
   * @param {string} variantId
   * @param {number} quantity
   * @param {Object} [properties]
   * @returns {Promise<any>}
   */
  /**
   * Dispatch cart:refresh event with a delay to allow Shopify Function time to apply discounts
   * @param {any} cfg
   * @param {string} source - Where the dispatch is coming from (for logging)
   */
  const dispatchCartRefresh = (cfg, source = 'dor-bogo') => {
    setTimeout(() => {
      if (cfg.debug) console.log('🔄 DOR_BOGO: Dispatching cart:refresh from ' + source + ' (delayed 1500ms)');
      try {
        const refreshEv = new CustomEvent('cart:refresh', { bubbles: true, cancelable: true });
        document.dispatchEvent(refreshEv);
      } catch {}
    }, 1500);
  };

  const addGift = async (variantId, quantity, properties) => {
    if (!variantId) return;

    const cfg = getConfig();
    const payload = { id: variantId, quantity };
    if (properties && typeof properties === 'object') {
      payload.properties = properties;
    }

    if (cfg.debug) {
      console.log('🎁 DOR_BOGO: addGift() CALLED', {
        variantId,
        quantity,
        properties
      });
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      }).then(r => r.json());

      if (cfg.debug) console.log('🎁 DOR_BOGO: addGift() SUCCESS');

      const cart = await fetchCart();

      // Dispatch cart:refresh for CartCount and other refresh listeners
      // Delayed to give Shopify Function time to apply discount
      dispatchCartRefresh(cfg, 'addGift()');

      return cart;
    } catch (err) {
      console.error('❌ DOR_BOGO: addGift error', err);
      throw err;
    }
  };

  /**
   * Update cart line quantities to remove existing gifts or set proper gift quantity.
   * Gift lines are detected by a special property on the line's properties.
   * @param {Array<any>} cartItems
   * @param {string} giftMarker
   * @param {string} giftVariantId
   * @param {number} desiredQuantity
   * @returns {Promise<any>}
   */
  const reconcileGifts = async (cartItems, giftMarker, giftVariantId, desiredQuantity) => {
    if (!giftMarker) return;

    const cfg = getConfig();
    if (cfg.debug) {
      console.log('🔍 DOR_BOGO: reconcileGifts() CALLED', {
        giftVariantId,
        desiredQuantity,
        totalCartItems: cartItems.length
      });
    }

    let madeChanges = false;
    const updates = [];
    let foundMatchingGift = false;

    // Scan all cart items for gift lines (detected by marker or 100% discount)
    cartItems.forEach(item => {
      const isGift = isGiftLine(item, giftVariantId, giftMarker);

      if (cfg.debug) {
        const originalPrice = Number(item.original_line_price || item.line_price || 0);
        const finalPrice = Number(item.final_line_price || item.line_price || 0);
        const discountPercent = originalPrice > 0 ? ((originalPrice - finalPrice) / originalPrice) * 100 : 0;

        console.log('  📦 Scanning item:', {
          id: item.id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          isGift,
          discountPercent: discountPercent.toFixed(1) + '%',
          originalPrice,
          finalPrice,
          title: item.title
        });
      }

      if (!isGift) return; // Skip non-gift items

      const itemVariantId = String(item.variant_id);
      const targetVariantId = String(giftVariantId);
      const currentQty = Number(item.quantity || 0);

      if (itemVariantId === targetVariantId) {
        // This is a gift line for our target variant
        foundMatchingGift = true;
        // Only queue update if quantity needs to change
        if (currentQty !== desiredQuantity) {
          if (cfg.debug) {
            console.log('  ✏️ Queueing update for matching gift:', {
              id: item.id,
              from: currentQty,
              to: desiredQuantity
            });
          }
          updates.push({ id: item.id, quantity: desiredQuantity });
          madeChanges = true;
        }
      } else {
        // This is a gift line for a DIFFERENT variant - remove it
        if (currentQty !== 0) {
          if (cfg.debug) {
            console.log('  🗑️ Queueing removal of different variant gift:', {
              id: item.id,
              variant: itemVariantId,
            giftVariantId: targetVariantId
            });
          }
          updates.push({ id: item.id, quantity: 0 });
          madeChanges = true;
        }
      }
    });

    // IMPORTANT: Handle the case where no gifts are needed (desiredQuantity=0).
    // When Shopify Function removes discount from orphaned items (because condition no longer met),
    // we lose the ability to identify them as gifts. So we must remove ALL items of giftVariantId
    // from the cart when no gift is needed.
    if (desiredQuantity === 0) {
      const allGiftVariantItems = cartItems.filter(item => {
        const isGiftVariant = String(item.variant_id) === String(giftVariantId);
        const hasQuantity = Number(item.quantity || 0) > 0;
        return isGiftVariant && hasQuantity;
      });

      if (allGiftVariantItems.length > 0) {
        if (cfg.debug) {
          console.log('🧹 DOR_BOGO: Removing all giftVariantId items (no gifts needed):', {
            count: allGiftVariantItems.length,
            items: allGiftVariantItems.map(i => ({
              id: i.id,
              quantity: i.quantity,
              discountPercent: ((Number(i.original_line_price || i.line_price || 0) - Number(i.final_line_price || i.line_price || 0)) / (Number(i.original_line_price || i.line_price || 1)) * 100).toFixed(1) + '%'
            }))
          });
        }
        allGiftVariantItems.forEach(item => {
          updates.push({ id: item.id, quantity: 0 });
          madeChanges = true;
        });
      }
    }

    if (cfg.debug) {
      console.log('🔍 DOR_BOGO: reconcileGifts() SCAN COMPLETE', {
        foundMatchingGift,
        updatesQueued: updates.length,
        madeChanges
      });
    }

    // Handle case: no gift line exists for our variant and we want some
    if (!foundMatchingGift && desiredQuantity > 0) {
      const props = { [giftMarker]: 'true' };
      if (cfg.debug) {
        console.log('➕ DOR_BOGO: No matching gift found, will call addGift()', {
          variant: giftVariantId,
          quantity: desiredQuantity
        });
      }
      // addGift will dispatch cart:refresh internally
      return addGift(giftVariantId, desiredQuantity, props);
    }

    // If no updates needed, skip the API call and event dispatch
    if (updates.length === 0) {
      if (cfg.debug) console.log('⏭️ DOR_BOGO: reconcileGifts() - no updates needed');
      return;
    }

    // Build updates object per Shopify API
    const body = { updates: {} };
    updates.forEach(u => { body.updates[u.id] = u.quantity; });

    if (cfg.debug) console.log('📤 DOR_BOGO: Sending updates to /cart/update.js', body);

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(r => r.json());

      if (cfg.debug) console.log('✅ DOR_BOGO: Update response received');

      const cart = await fetchCart();

      // Only dispatch cart:refresh if we actually made changes
      // Delayed to give Shopify Function time to apply discounts
      if (madeChanges) {
        dispatchCartRefresh(cfg, 'reconcileGifts()');
      }

      return cart;
    } catch (err) {
      console.error('❌ DOR_BOGO: reconcileGifts error', err);
      throw err;
    }
  };

  /**
   * Track if evaluation is currently in progress to prevent concurrent evaluations
   */
  let isEvaluating = false;

  /**
   * Main evaluation function: fetch cart, evaluate groups, reconcile gift
   */
  const evaluate = async () => {
    const cfg = getConfig();
    if (cfg.debug) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔄 DOR_BOGO: evaluate() START', {
        timestamp: new Date().toISOString(),
        isEvaluating
      });
    }

    // Prevent concurrent evaluations - skip if one is already running
    if (isEvaluating) {
      if (cfg.debug) console.log('⛔ DOR_BOGO: evaluation already in progress, SKIPPING');
      return;
    }

    isEvaluating = true;

    try {
      const giftMf = cfg.gift_variant || {};
      const giftVariantId = resolveVariantId(giftMf.jsonValue || giftMf.value || cfg.gift_variant);
      const giftMultiplier = parseNumberConfig(cfg.gift_multiplier, 1);
      const giftMarker = cfg.gift_marker || '_dor_bogo_gift';

      const group1 = {
        variants: parseListMetafield(cfg.group1_variants),
        list: parseListMetafield(cfg.group1_list),
        match: (cfg.group1_match?.value) || cfg.group1_match || 'tag'
      };
      const group2 = {
        variants: parseListMetafield(cfg.group2_variants),
        list: parseListMetafield(cfg.group2_list),
        match: (cfg.group2_match?.value) || cfg.group2_match || 'variant'
      };

      if (cfg.debug) {
        console.log('⚙️ DOR_BOGO: Config parsed', {
          group1,
          group2,
          giftVariantId,
          giftMultiplier,
          giftMarker
        });
      }

      const cart = await fetchCart();

      if (cfg.debug) {
        console.log('🛒 DOR_BOGO: Cart fetched', {
          itemCount: cart.item_count,
          items: cart.items?.map(i => {
            const originalPrice = Number(i.original_line_price || i.line_price || 0);
            const finalPrice = Number(i.final_line_price || i.line_price || 0);
            const discountPercent = originalPrice > 0 ? ((originalPrice - finalPrice) / originalPrice) * 100 : 0;
            return {
              id: i.id,
              variant_id: i.variant_id,
              quantity: i.quantity,
              title: i.title,
              discountPercent: discountPercent.toFixed(1) + '%',
              isGift: isGiftLine(i, giftVariantId, giftMarker)
            };
          })
        });
      }

      // Collect product handles for items missing tags
      const handlesToFetch = [];
      const handleSeen = {};

      cart.items?.forEach(item => {
        const hasTags = item.tags
          ? (typeof item.tags === 'string' ? item.tags.trim() !== '' : Array.isArray(item.tags) && item.tags.length > 0)
          : false;

        if (!hasTags && item.handle && !handleSeen[item.handle]) {
          handleSeen[item.handle] = true;
          handlesToFetch.push(item.handle);
        }
      });

      // Fetch product data for each handle in parallel (small carts expected)
      const results = await Promise.all(
        handlesToFetch.map(h =>
          fetchProductByHandle(h).then(product => ({ handle: h, product }))
        )
      );

      // Populate tags on cart items where product data found
      results.forEach(({ product, handle }) => {
        if (product?.tags) {
          const tagsStr = typeof product.tags === 'string'
            ? product.tags
            : Array.isArray(product.tags)
              ? product.tags.join(',')
              : '';

          cart.items?.forEach(it => {
            if (it.handle === handle) it.tags = tagsStr;
          });

          if (cfg.debug) console.debug('DOR_BOGO: populated tags for', handle, tagsStr);
        }
      });

      const lines = normalizeCart(cart);

      // IMPORTANT: Exclude existing gift lines from matching.
      // Gift lines are detected by checking for 100% discount (applied by Shopify Function).
      // This prevents gift items from counting towards trigger conditions,
      // allowing BOGO promos where gift variant = trigger variant (e.g., "Buy 2 X, Get 1 X free").
      // We must filter by lineIndex, not id, since gift and non-gift of same variant share same id.
      const giftLineIndexes = (cart.items || [])
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => isGiftLine(item, giftVariantId, giftMarker))
        .map(({ idx }) => idx);

      const linesNoGifts = lines.filter(l => !giftLineIndexes.includes(l.lineIndex));

      if (cfg.debug) {
        console.log('🎁 DOR_BOGO: Gift items identified', {
          giftLineIndexes,
          totalLines: lines.length,
          linesAfterExclusion: linesNoGifts.length
        });
      }

      const g1 = countMatches(linesNoGifts, group1);
      const g2 = countMatches(linesNoGifts, group2);

      const giftsToApply = Math.min(g1, g2) * giftMultiplier;

      // Calculate existing gift count for giftVariantId (using discount detection)
      let existingGiftCount = 0;
      const existingGiftLines = cart.items?.filter(i => isGiftLine(i, giftVariantId, giftMarker)) || [];

      existingGiftLines.forEach(i => {
        existingGiftCount += Number(i.quantity || 0);
      });

      if (cfg.debug) {
        console.log('📊 DOR_BOGO: Match counts', { g1, g2, giftsToApply, existingGiftCount });
      }

      if (giftsToApply === existingGiftCount) {
        if (cfg.debug) {
          console.log('✅ DOR_BOGO: No change required');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        }
        return;
      }

      if (cfg.debug) {
        console.log('🔧 DOR_BOGO: Reconciliation needed', {
          desired: giftsToApply,
          existing: existingGiftCount
        });
      }

      const res = await reconcileGifts(cart.items || [], giftMarker, giftVariantId, giftsToApply);
      if (cfg.debug) {
        console.log('🏁 DOR_BOGO: Reconcile completed');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      }
      return res;
    } catch (err) {
      console.error('❌ DOR_BOGO: evaluate error', err);
      if (cfg.debug) console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    } finally {
      isEvaluating = false;
    }
  };

  /**
   * Create a debounced version of evaluate to prevent excessive re-evaluations
   * when cart:change fires multiple times in rapid succession (e.g., drawer open/close)
   */
  let evaluateTimeout = null;
  const debouncedEvaluate = () => {
    if (evaluateTimeout) clearTimeout(evaluateTimeout);
    evaluateTimeout = setTimeout(() => {
      evaluateTimeout = null;
      evaluate();
    }, 300); // Wait 300ms after last cart:change event before evaluating
  };

  // Watch for cart changes: listen for theme CustomEvent 'cart:change' if present,
  // otherwise fall back to any Shopify hooks or polling.
  const start = () => {
    const cfg = getConfig();
    const pollInterval = Number(cfg.poll_interval || 10000);

    if (cfg.debug) console.log('🚀 DOR_BOGO: Starting, poll interval:', pollInterval, 'ms');

    evaluate();

    // Prefer theme custom event 'cart:change' (found in theme.js.liquid)
    // Use debounced version to handle rapid drawer open/close events
    try {
      document.addEventListener('cart:change', () => {
        if (cfg.debug) console.log('📢 DOR_BOGO: cart:change event received');
        debouncedEvaluate();
      });
    } catch {}

    // Older/custom themes may expose Shopify.onCartUpdate or CartJS hooks
    if (typeof window.Shopify !== 'undefined' && window.Shopify.onCartUpdate) {
      try {
        window.Shopify.onCartUpdate = () => debouncedEvaluate();
      } catch {}
    }

    // As a safe fallback, poll at configured interval
    setInterval(evaluate, pollInterval);
  };

  // Start after DOM ready
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
